import React, { useRef, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from '@/lib/utils';
import { ChevronDown, Braces } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface EmailVariable {
  value: string;
  label: string;
  category?: string;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  variables?: EmailVariable[];
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter text...",
  className,
  readOnly = false,
  variables
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const isUpdatingFromProps = useRef(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean']
      ],
    },
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link'
  ];

  const insertVariable = (variableValue: string) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) {
      console.warn('Quill editor not available');
      return;
    }
    
    // Focus the editor first to ensure we can get/set selection
    quill.focus();
    
    const selection = quill.getSelection();
    const cursorPosition = selection ? selection.index : quill.getLength() - 1;
    
    quill.insertText(cursorPosition, variableValue, 'user');
    quill.setSelection(cursorPosition + variableValue.length, 0);
    
    // Close dropdown after a small delay to ensure insertion completes
    setTimeout(() => {
      setDropdownOpen(false);
    }, 50);
  };
  
  // Update editor content when value prop changes (for controlled component behavior)
  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const currentContent = quill.root.innerHTML;
    const normalizedCurrent = currentContent.trim();
    const normalizedValue = (value || '').trim();
    
    // Only update if the value actually changed to avoid unnecessary updates
    // Compare normalized values to handle whitespace differences
    if (normalizedCurrent !== normalizedValue) {
      isUpdatingFromProps.current = true;
      
      // Save current scroll position to prevent scroll jump
      const scrollY = window.scrollY;
      
      // Use dangerouslyPasteHTML with 'silent' source to prevent scroll/focus behavior
      quill.clipboard.dangerouslyPasteHTML(0, value || '', 'silent');
      
      // Restore scroll position after content update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
      
      // Reset flag after a brief delay to allow the update to complete
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [value]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;

    const parseMarkdownToOps = (text: string) => {
      const ops: any[] = [];
      // Normalize whitespace and put bullets on new lines
      const normalized = text
        .replace(/\r\n/g, "\n")
        .replace(/\n{2,}/g, "\n")
        .replace(/\s-\s/g, "\n• ");
      const parts = normalized.split(/(\*\*[^*]+\*\*)/g);
      parts.forEach((part) => {
        if (!part) return;
        const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
        if (boldMatch) {
          ops.push({ insert: boldMatch[1], attributes: { bold: true } });
        } else {
          ops.push({ insert: part });
        }
      });
      return ops;
    };

    quill.clipboard.addMatcher(Node.TEXT_NODE, (_node, delta) => {
      const newOps: any[] = [];
      delta.ops?.forEach((op: any) => {
        if (typeof op.insert === 'string') {
          const str: string = op.insert as string;
          let lastIndex = 0;
          let match: RegExpExecArray | null;
          while ((match = urlRegex.exec(str)) !== null) {
            const index = match.index;
            const url = match[0];
            if (index > lastIndex) {
              newOps.push(...parseMarkdownToOps(str.slice(lastIndex, index)));
            }
            const href = url.startsWith('http') ? url : `http://${url}`;
            newOps.push({ insert: url, attributes: { link: href } });
            lastIndex = index + url.length;
          }
          if (lastIndex < str.length) {
            newOps.push(...parseMarkdownToOps(str.slice(lastIndex)));
          }
        } else {
          newOps.push(op);
        }
      });
      delta.ops = newOps;
      return delta;
    });
  }, []);

  // Group variables by category
  const groupedVariables = variables?.reduce((acc, variable) => {
    const category = variable.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(variable);
    return acc;
  }, {} as Record<string, EmailVariable[]>);

  return (
    <div className={cn("rich-text-editor", readOnly && "read-only", className)}>
      {/* Variables dropdown - placed above the editor */}
      {variables && variables.length > 0 && !readOnly && (
        <div className="flex justify-end mb-1">
          <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 px-2 gap-1 text-xs font-normal"
                type="button"
              >
                <Braces className="h-3.5 w-3.5" />
                Insert Variable
                <ChevronDown className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              align="end" 
              className="w-56 p-0 h-[280px]"
              style={{ zIndex: 9999 }}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <ScrollArea className="h-full w-full">
                <div className="p-1">
                  {groupedVariables && Object.entries(groupedVariables).map(([category, vars], idx) => (
                    <div key={category}>
                      {idx > 0 && <div className="my-1 h-px bg-border" />}
                      <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{category}</div>
                      {vars.map((variable) => (
                        <button
                          key={variable.value}
                          type="button"
                          onClick={() => {
                            insertVariable(variable.value);
                          }}
                          className="flex w-full justify-between items-center px-2 py-1 text-xs rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                        >
                          <span>{variable.label}</span>
                          <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{variable.value}</code>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>
      )}
      
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={(content) => {
          // Only call onChange if we're not updating from props
          if (!isUpdatingFromProps.current) {
            onChange(content);
          }
        }}
        placeholder={placeholder}
        modules={readOnly ? { toolbar: false } : modules}
        formats={formats}
        readOnly={readOnly}
        style={{
          minHeight: readOnly ? 'auto' : '200px',
        }}
      />
      <style>{`
        .rich-text-editor .ql-editor {
          min-height: 200px;
          font-size: 14px;
          line-height: 1.8;
        }
        .rich-text-editor .ql-editor p {
          margin-bottom: 1em;
        }
        .rich-text-editor .ql-editor h1,
        .rich-text-editor .ql-editor h2,
        .rich-text-editor .ql-editor h3 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        .rich-text-editor .ql-editor ul,
        .rich-text-editor .ql-editor ol {
          margin-bottom: 1em;
          padding-left: 1.5em;
        }
        .rich-text-editor .ql-editor li {
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ql-container {
          font-family: inherit;
        }
        .rich-text-editor.read-only .ql-container {
          border: none;
        }
        .rich-text-editor.read-only .ql-editor {
          padding: 0;
          min-height: auto;
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
